import { useSelector } from "react-redux";
import {
  CCDropdown,
  CustomInput,
  CustomSelect,
} from "../design/components/customComponents";
import { useEffect, useState } from "react";
import styles from "./BillingAddressForm.module.css";
import axios from "axios";
import { Autocomplete, Box, TextField } from "@mui/material";
import colors from "../design/colors";
import AnimationPage from "../shared/animation-page/AnimationPage";
import { dropdownIcon, topNavAccountSelectedAccountTickIcon } from "../../utility-functions/assetLoader";
import { enqueueSnackbar } from "notistack";
import { states as statesInIndia } from "./India-states";

const states = Object.keys(statesInIndia).map((state) => {
  return { displayValue: state, value: state };
});

const BillingAddressForm = ({
  companyName,
  address,
  gstNumber,
  handleGstNumberChange,
  handleCompanyNameChange,
  handleAddressChange,
  gstOptional,
  billingNumber,
  handleBillingNumberChange,
  countryCodeDrpdown,
  handleCountryCodeDropdownChange,
  ...props
}) => {
  const [countries, setCountries] = useState([]);
  const [value, setValue] = useState({ label_en: "India", iso2_code: "IN" });
  const [inputValue, setInputValue] = useState("India");

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (props.activeTab !== "Billing" && !props.visible) return;
    (async () => {
      try {
        setShowLoader(true);
        let url = `https://public.opendatasoft.com/api/explore/v2.1/catalog/datasets/countries-codes/records?select=iso2_code%2C%20label_en&limit=100`;
        const res = await Promise.all([
          axios.get(url),
          axios.get(url + "&offset=100"),
          axios.get(url + "&offset=200"),
        ]);
        const data = res.map((res) => res.data.results);
        setCountries(data.flat());
      } catch {
        setCountries([]);
        enqueueSnackbar("Could not retrieve list of countries", {
          variant: "error",
        });
      } finally {
        setShowLoader(false);
      }
    })();
  }, [props.activeTab, props.visible]);

  useEffect(() => {
    if (!countries || !countries.length || !address || !address.country) return;
    const country = countries.find(
      (elem) =>
        elem.iso2_code === address.country || elem.label_en === address.country
    );
    if (country) {
      setValue(country);
      setInputValue(country.label_en);
    }
  }, [countries, address]);

  const reduxAccount = useSelector((state) => state?.account?.value);
  useEffect(() => {
    if (!reduxAccount) return;
    const selectedOrg = reduxAccount?.selectedOrg;
    if (selectedOrg) {
      const selectedOrgDetails = reduxAccount.allOrgs?.find(
        (org) => org.id === selectedOrg
      );
      handleCompanyNameChange("value", selectedOrgDetails?.company);
    } else {
      handleCompanyNameChange("value", reduxAccount?.allOrgs[0]?.company || "");
    }
  }, [reduxAccount]);

  if (showLoader) return <AnimationPage />;

  return (
    <div className={styles.billingInfoForm} style={{ width: props.formWidth }}>
      <CustomInput
        label="Company name"
        name="company"
        type="text"
        value={companyName}
        handleChange={handleCompanyNameChange}
        handleFocus={() => handleCompanyNameChange("focus")}
        style={{ width: "100%" }}
      />
      <label style={{width: '100%'}}>
        <span className="t7 medium-font">Address</span>
        <input
          className={styles.input}
          type="text"
          placeholder="Address line 1"
          value={address.line1}
          name="line1"
          onChange={handleAddressChange}
        />
        <input
          className={styles.input}
          type="text"
          placeholder="Address line 2"
          value={address.line2}
          name="line2"
          onChange={handleAddressChange}
        />
        <div className={styles.flexContainer}>
          <input
            className={styles.input}
            type="text"
            placeholder="City"
            value={address.city}
            name="city"
            onChange={handleAddressChange}
          />
          <input
            className={styles.input}
            type="text"
            placeholder="ZIP Code"
            value={address.zip}
            name="zip"
            onChange={handleAddressChange}
          />
        </div>
        <div className={styles.flexContainer}>
          {!["India", "IN"].includes(address.country) ? (
            <input
              disabled={!address.country}
              className={styles.input}
              style={{ height: "42px" }}
              type="text"
              placeholder="State"
              value={address.state}
              name="state"
              onChange={handleAddressChange}
            />
          ) : (
            <div className={`select-container ${styles.stateSelect}`}>
              <CustomSelect
                label=""
                name="state"
                value={address.state}
                handleChange={handleAddressChange}
                renderVal={(val) => {
                  return val ? (
                    <span className="nc-gray-900 t7 regular-font">
                      {states.find((elem) => elem.value === val)?.displayValue}
                    </span>
                  ) : (
                    <span className="nc-gray-400 t7 regular-font"> State </span>
                  );
                }}
                options={states}
                menuWidth={props.formWidth / 2}
                selectStyles={{ maxWidth: props.formWidth / 2 }}
              />
            </div>
          )}
          <Autocomplete
            id="country-select"
            disabled={props?.stateRequired}
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              fontFamily: "Inter",
              color: colors.nc_gray_900,
              width: '50%',
              height: "42px",
              display: "inline-block",
              margin: "0 0 24px",
              "& .MuiFormControl-root": {
                height: "100%",
              },
              "& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd.MuiAutocomplete-input.MuiAutocomplete-inputFocused.MuiInputBase-input-MuiOutlinedInput-input":
              {
                fontFamily: "Inter",
                fontWeight: "400",
              },
              "& .MuiInputBase-root": {
                height: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                flexWrap: "nowrap",
                alignItems: "center",
                fontFamily: "Inter",
                ".Mui-focused:": {
                  border: `1px solid ${colors.ic_green_500}`,
                  boxShadow: "0px 0px 0px 4px #F0FCF4",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${colors.nc_gray_300}`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${colors.nc_gray_300}`,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${colors.ic_green_300}`,
              },
              ".Mui-focused": {
                border: `1px solid ${colors.ic_green_300}`,
                outline: "none",
              },
            }}
            disableClearable
            popupIcon={dropdownIcon()}
            ListboxProps={{
              sx: {
                ...localStyles.scrollbar,
                border: "1px solid #E4E4E7",
                maxHeight: '250px'
              }
            }}
            options={countries}
            value={value}
            onChange={(event, newValue) => {
              handleAddressChange(newValue?.label_en, "country");
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            autoHighlight
            getOptionLabel={(option) => option.label_en}
            renderOption={(props, option) => (
              <Box
                component="div"
                {...props}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "14px",
                }}
              >
                <span>{option.label_en}</span>
                <div style={{ position: "absolute", right: "6px" }}>
                  {value?.label_en === option?.label_en &&
                    topNavAccountSelectedAccountTickIcon()}
                </div>
              </Box>
            )}
            selectOnFocus={false}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Country"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password", // disable autocomplete and autofill
                }}
                InputLabelProps={{ shrink: false }}
              />
            )}
          />
        </div>
      </label>
      <label style={{width: '100%'}}>
        <div className="connected-to-pf">Billing Number</div>

        <div>
          <CustomInput
            style={{ marginTop: "0px", height: "36px", marginBottom: "0px",  width: props.formWidth, }}
            dropdown={
              <CCDropdown
                CCDropdownstyle={{
                  marginTop: "0px",
                  border: "none",
                }}
                selectedCountryCode={countryCodeDrpdown}
                handleCountrySelect={(val) => handleCountryCodeDropdownChange(val)}
              />
            }
            value={billingNumber}
            handleChange={(e) =>
              handleBillingNumberChange({ value: e.target.value })
            }
          />
        </div>
      </label>
      <label style={{width: '100%'}}>
        <span className="t7 medium-font">
          GST number{" "}
          <span
            className="t7 regular-font"
            style={{ color: colors.nc_gray_700 }}
          >
            {gstOptional && "(Optional)"}
          </span>
        </span>
        <input
          className={styles.input}
          style={{width: props.formWidth}}
          type="text"
          value={gstNumber}
          onChange={(e) => handleGstNumberChange(e.target.value)}
        />
      </label>
    </div>
  );
};

export default BillingAddressForm;

const localStyles = {
  scrollbar: {
    "&::-webkit-scrollbar": {
      background: "transparent",
      width: "8px",
      opacity: 0,
      height: 0,
    },
    "&::-webkit-scrollbar-track-piece": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      width: "8px",
      borderRadius: "3px",
      transition: "all 0.3s ease-in-out",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
      width: "8px",
    },
    "&::-webkit-scrollbar-corner": {
      display: "none"
    },
    "&::-webkit-scrollbar-button": {
      display: "none"
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: "#c6ccc8"
    }
  }
}
